import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Loader2, Camera, CameraOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export const QRScanner = () => {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);

  const validate = async (value) => {
    const c = (value ?? code).trim();
    if (!c) { toast.error("Enter or scan a ticket code"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/admin/validate", { code: c });
      setResult(data);
      if (data.result === "VALID") toast.success("Valid ticket");
      else if (data.result === "ALREADY_USED") toast.warning("Already used");
      else toast.error(data.message || "Invalid ticket");
    } catch { toast.error("Validation failed"); }
    setLoading(false);
  };

  const markUsed = async () => {
    const c = result?.booking?.qr_token || result?.booking?.booking_id;
    if (!c) return;
    try {
      const { data } = await api.post("/admin/mark-used", { code: c });
      if (data.result === "MARKED_USED") { toast.success("Marked as used"); validate(c); }
      else toast.warning(data.message || data.result);
    } catch { toast.error("Failed to mark used"); }
  };

  const stopScanner = async () => {
    const s = scannerRef.current;
    if (s) {
      try { await s.stop(); await s.clear(); } catch {}
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const startScanner = async () => {
    setScanning(true);
    setResult(null);
    // wait a tick for the #qr-reader div to mount
    setTimeout(async () => {
      try {
        const html5 = new Html5Qrcode("qr-reader");
        scannerRef.current = html5;
        await html5.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 230, height: 230 } },
          async (decodedText) => {
            setCode(decodedText);
            await stopScanner();
            validate(decodedText);
          },
          () => {}
        );
      } catch (e) {
        toast.error("Could not access camera. Use manual entry.");
        setScanning(false);
      }
    }, 150);
  };

  useEffect(() => () => { stopScanner(); }, []);

  const color = result?.result === "VALID" ? "text-green-400 border-green-500/40 bg-green-500/10"
    : result?.result === "ALREADY_USED" ? "text-amber-400 border-amber-500/40 bg-amber-500/10"
    : "text-red-400 border-red-500/40 bg-red-500/10";

  return (
    <div className="max-w-xl" data-testid="qr-scanner">
      <p className="text-sm text-zinc-400">Scan the ticket QR with the camera, or type the Booking ID / QR code to verify entry.</p>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <input className="input-field" placeholder="Booking ID (e.g. SRK12AB34) or QR code"
          value={code} onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && validate()} data-testid="admin-qr-scanner-input" />
        <button onClick={() => validate()} disabled={loading} className="btn-gold !px-6" data-testid="qr-validate-button">
          {loading ? <Loader2 className="animate-spin" size={16} /> : "VERIFY"}
        </button>
      </div>

      <button onClick={() => (scanning ? stopScanner() : startScanner())} className="btn-outline mt-3 !py-2.5 text-sm" data-testid="qr-camera-toggle">
        {scanning ? <><CameraOff size={15} /> Stop Camera</> : <><Camera size={15} /> Scan with Camera</>}
      </button>

      {scanning && <div id="qr-reader" className="mt-4 overflow-hidden rounded-lg border border-[#2A2A32]" style={{ width: "100%" }} />}

      {result && (
        <div className={`mt-6 rounded-lg border p-6 ${color}`} data-testid="qr-result">
          <p className="font-serif text-2xl font-bold" data-testid="qr-result-status">{result.result.replace("_", " ")}</p>
          {result.booking && (
            <div className="mt-3 space-y-1 text-sm text-zinc-300">
              <p><span className="text-zinc-500">Name:</span> {result.booking.first_name}</p>
              <p><span className="text-zinc-500">Event:</span> {result.booking.event_name}</p>
              <p><span className="text-zinc-500">Category:</span> {result.booking.category_name} × {result.booking.quantity}</p>
              <p><span className="text-zinc-500">Booking ID:</span> {result.booking.booking_id}</p>
              {result.used_at && <p><span className="text-zinc-500">Used at:</span> {new Date(result.used_at).toLocaleString()}</p>}
            </div>
          )}
          {result.message && <p className="mt-2 text-sm">{result.message}</p>}
          {result.result === "VALID" && (
            <button onClick={markUsed} className="btn-gold mt-4" data-testid="mark-used-button">MARK AS USED</button>
          )}
        </div>
      )}
    </div>
  );
};
